import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { Destination } from '@ui/__generated__/graphql'
import { ApolloErrorMessage } from '@ui/components/core/ApolloErrorMessage'
import { TableList, TableListHeader } from '@ui/components/elements/TableList'
import { Spinner } from '@ui/components/loaders'
import { DeleteDestinationQuery, GetDestinationsQuery } from '@ui/loaders/destinations'
import { useAuth } from '@ui/providers/AuthProvider.tsx'
import { useNavigationStateStore } from '@ui/stores'

export const Destinations = () => {
  const { data, loading, error, refetch } = GetDestinationsQuery()
  const [deleteDestination] = DeleteDestinationQuery()

  const { isDemoMode } = useAuth()

  const navigate = useNavigate()
  const { instanceId } = useNavigationStateStore(useShallow((state) => state.params))

  const onNavigateToDestination = (destinationId: Destination['id']) => {
    navigate(`/instances/${instanceId}/destinations/${destinationId}`)
  }

  const onDeleteDestination = useCallback(
    async (destinationId: Destination['id']) => {
      if (isDemoMode) return

      await deleteDestination({ variables: { input: { id: destinationId } } })
      await refetch()
    },
    [deleteDestination, isDemoMode, refetch]
  )

  const destinations = useMemo(() => {
    return data?.destinations.map((destination: Destination) => ({
      id: destination.id,
      name: destination.name,
      type: [destination.template.slug || ''],
      apiKey: destination.formData?.['api-key'],
    }))
  }, [data])

  const menuItems = useMemo(() => {
    return [
      {
        label: 'Delete',
        onClick: (destinationId: string) => onDeleteDestination(destinationId),
      },
    ]
  }, [onDeleteDestination])

  if (error) {
    return <ApolloErrorMessage error={error} />
  }

  if (loading) {
    return <Spinner />
  }

  return (
    <div className="h-full w-full">
      <>
        <div className="h-full w-full space-y-4">
          <TableListHeader title="Destinations" length={data?.destinations.length ?? 0} />

          <TableList
            items={destinations ?? []}
            menuItems={menuItems}
            logoColumns={['type']}
            onRowClick={(destinationId) => onNavigateToDestination(destinationId)}
          />
        </div>
      </>
    </div>
  )
}
